'use client';

/**
 * PurchaseFlow Component
 *
 * Main component managing the purchase flow state and navigation.
 * Orchestrates screens for payment selection, addition, and processing.
 *
 * Features:
 * - Multi-screen navigation with back/close support
 * - Payment method selection and addition
 * - PAYG (Pay-As-You-Go) validation mode
 * - Mobile app integration via mobileAppProps
 * - Analytics tracking via AnalyticsProvider
 * - Processor mounted as sibling (prevents remounting on UI changes)
 *
 * Architecture:
 * - PurchaseFlow: Wrapper that provides analytics context
 * - PurchaseFlowContent: Thin orchestrator that delegates to display-mode components
 * - usePurchaseFlowCore: Hook containing all state, effects, and handlers
 * - ButtonModeContent: Button display mode renderer
 * - StandardModeContent: Modal / inline display mode renderer
 * - ProcessorElement: Mounted as sibling, renders null/hidden container
 */

import React, { useMemo } from 'react';
import type { PurchaseFlowProps } from './types';
import ErrorPopup from './components/ErrorPopup';
import { ProcessorElement } from './components/ProcessorElement';
import ButtonModeContent from './components/ButtonModeContent';
import StandardModeContent from './components/StandardModeContent';
import { usePurchaseFlowCore } from './hooks/usePurchaseFlowCore';
import { PURCHASE_FLOW_ICONS, PURCHASE_FLOW_TEXT_KEYS } from './utils/constants';
import {
  PurchaseFlowConfigProvider, usePurchaseRender
} from './config/PurchaseFlowConfigProvider';

// ============================================================================
// Main Content Component (orchestrator)
// ============================================================================

const PurchaseFlowContent: React.FC<Omit<PurchaseFlowProps, 'config'>> = (props) => {
  const {
    isPayg = false,
    bannerSlot,
    promoFooterSlot,
    purchaseType,
    metadata,
    displayMode = 'inline',
    mode = 'purchase',
    successButtonTextKey,
    mobileAppProps,
    children,
    buttonClassName,
    showStatusPopup = true,
    disabled = false,
    onFlowStarted
  } = props;

  const core = usePurchaseFlowCore(props);
  const { renderIcon, renderText } = usePurchaseRender();

  // Processor mounted as sibling so it doesn't remount on UI changes
  // (e.g. Popover open). Starts only when startSignal increments.
  const processorElement = (
    <ProcessorElement
      startSignal={ core.startSignal }
      retryKey={ core.retryKey }
      selectedPaymentMethod={ core.selectedPaymentMethod }
      amount={ core.creditsRequested }
      withBonus={ core.creditsRequested > core.price }
      purchaseType={ purchaseType }
      metadata={ metadata }
      recover={ core.recoverFlag }
      recoverBaseUrl={ core.recoverBaseUrl }
      clickContext={ core.clickContextRef.current }
      fxRate={ core.fxRate }
      currency={ core.currency?.key }
      onStatusChange={ core.processorCallbacks.handleStatusChange }
      onSuccess={ core.processorCallbacks.handleSuccess }
      onError={ core.processorCallbacks.handleError }
      onCancel={ core.processorCallbacks.handleCancel }
      onDismiss={ core.processorCallbacks.handleDismiss }
      onReady={ core.handleNativeButtonReady }
      nativeTriggerRef={ core.nativeTriggerRef }
    />
  );

  const validationErrorInfo = !mobileAppProps && core.validationError ? {
    title: renderText(PURCHASE_FLOW_TEXT_KEYS.VALIDATION_ERROR_TITLE) as string,
    message: renderText(PURCHASE_FLOW_TEXT_KEYS.VALIDATION_ERROR_MESSAGE) as string
  } : undefined;

  const validationErrorPopup = (
    <ErrorPopup
      error={ validationErrorInfo }
      onDismiss={ core.dismissValidationError }
      onBack={ core.handleValidationErrorBack }
      icon={ renderIcon(PURCHASE_FLOW_ICONS.VALIDATION_ERROR) }
      okLabel={ renderText(PURCHASE_FLOW_TEXT_KEYS.BUTTON_OK) as string }
    />
  );

  // ==========================================================================
  // Render: delegate to display-mode component
  // ==========================================================================

  if (displayMode === 'button') {
    return (
      <ButtonModeContent
        core={ core }
        metadata={ metadata }
        disabled={ disabled }
        buttonClassName={ buttonClassName }
        showStatusPopup={ showStatusPopup }
        successButtonTextKey={ successButtonTextKey }
        processorElement={ processorElement }
        onFlowStarted={ onFlowStarted }
      >
        {children}
      </ButtonModeContent>
    );
  }

  return (
    <StandardModeContent
      core={ core }
      displayMode={ displayMode || 'inline' }
      isPayg={ isPayg }
      mode={ mode }
      bannerSlot={ bannerSlot }
      promoFooterSlot={ promoFooterSlot }
      successButtonTextKey={ successButtonTextKey }
      mobileAppProps={ mobileAppProps }
      metadata={ metadata }
      processorElement={ processorElement }
      validationErrorPopup={ validationErrorPopup }
    />
  );
};

// ============================================================================
// Main Component (with Config Provider)
// ============================================================================

const PurchaseFlow: React.FC<PurchaseFlowProps> = ({
  config,
  price,
  creditsRequested,
  bonus,
  isPayg,
  fxRate,
  currencyKey,
  supportedCurrencies,
  metadata,
  ...rest
}) => {
  const analyticsConfig = useMemo(() => ({
    ...config.analytics,
    price,
    bonus,
    isPayg,
    ...(metadata?.clickSource != null && { clickSource: metadata.clickSource }),
    ...(metadata?.purchaseScreen != null && { purchaseScreen: metadata.purchaseScreen }),
    displayCurrency: currencyKey ?? 'USD',
    fxRate
    // localAmount is set after /combined responds via analytics.updateConfig in usePurchaseFlowCore
  }), [config.analytics, price, bonus, isPayg, fxRate, currencyKey, metadata?.clickSource, metadata?.purchaseScreen]);

  return (
    <PurchaseFlowConfigProvider config={ config } analyticsConfig={ analyticsConfig }>
      <PurchaseFlowContent
        price={ price }
        creditsRequested={ creditsRequested }
        bonus={ bonus }
        isPayg={ isPayg }
        fxRate={ fxRate }
        currencyKey={ currencyKey }
        supportedCurrencies={ supportedCurrencies }
        metadata={ metadata }
        { ...rest }
      />
    </PurchaseFlowConfigProvider>
  );
};

export default PurchaseFlow;
